import { Component, computed, effect, ElementRef, HostListener, inject, input, signal, untracked, viewChild, ViewChild, ViewContainerRef, ViewRef, ɵunwrapWritableSignal } from '@angular/core';
import { AttributeMenuService } from '../../services/attribute-menu.service';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { CheckoutService } from '../../services/checkout.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { PriceProviderService } from 'src/app/providers/price/price.provider.service';
import { assert, assertTrue } from 'src/app/utils/assert.util';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { environment } from 'src/environments/environment';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { Navigation2Service } from '../../services/navigation2.service';
import { PriceService } from 'src/app/services/price/price.service';
import { StoneCountsProviderService } from 'src/app/providers/stone-counts/stone-counts.provider.service';
import { GemstoneArrangementWithSetting } from 'src/app/interfaces/gemstone-arrangement-with-setting';
import { GemstoneTypeWithShape } from 'src/app/interfaces/gemstone-type-with-shape';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { ProfileWeightsService } from 'src/app/services/profile-weights/profile-weights.service';
import { formatWeightThickness } from 'src/app/utils/weight-thickness-format';
import { ThumbnailProviderService } from 'src/app/providers/thumbnail/thumbnail.provider.service';
import { MainImageService } from 'src/app/services/main-image/main-image.service';
import { Subscription } from 'rxjs';
import { ShareService } from 'src/app/services/share/share.service';
import { IframeService } from 'src/app/services/iframe/iframe.service';

@Component({
  selector: 'app-checkout-summary',
  standalone: false,
  templateUrl: './checkout-summary.component.html',
  styleUrl: './checkout-summary.component.scss',
  host: {
    '[class.IsSharing]': 'isSharing()'
  }
})
export class CheckoutSummaryComponent {
  private iframeService = inject(IframeService);
  private mainImageService = inject(MainImageService);
  private thumbnailProvider = inject(ThumbnailProviderService);
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  private stoneCountsProvider = inject(StoneCountsProviderService);
  private priceService = inject(PriceService);
  private navigationService = inject(Navigation2Service);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private configuredItemService = inject(ConfiguredItemService);
  private priceProvider = inject(PriceProviderService);
  private ref = inject(ViewContainerRef)
  private attributeMenuService = inject(AttributeMenuService);
  private profileWeightsService = inject(ProfileWeightsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private checkoutService = inject(CheckoutService);
  private clipBoard = inject(Clipboard);
  private loadingService = inject(LoadingService);
  private shareService = inject(ShareService);
  private checkoutContainerRef = viewChild<ElementRef<HTMLDivElement>>('CheckoutContainer');
  shouldShowCopyConfirmation = signal(false);
  isSharing = input(false);
  skuToShowOverride = input<string | null>(null);
  skuToShow = signal<string | null>(null);
  actualSKUToShow = computed(() => this.skuToShowOverride() ?? this.skuToShow());
  isSKUToShowCustom = computed(() => {
    const skuToShow = this.actualSKUToShow();
    return !skuToShow || this.configuredItemService.isCustomSKU(skuToShow);
  });
  shareLinkTextArea = viewChild('shareLinkTextArea', { read: ElementRef<HTMLTextAreaElement> });
  itemNumber = computed(() => this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributes()));
  poValue: string | null = null;
  didAgree = false;
  windowHeight = signal(window.innerHeight);
  windowWidth = signal(window.innerWidth);
  stoneCount = signal<number | null>(null);
  shouldShowConsumerSiteShare = computed(() => this.checkoutService.shouldShowConsumerSiteShare());
  retailerName = computed(() => this.checkoutService.retailerName());
  shareMessage = computed(() => this.checkoutService.shareMessageOverride() ?? 'Copy link below to share. For availability of your ring, please email the shared link to this retailer (using their "contact us" page).');
  stoneArrangementWithSetting = signal<GemstoneArrangementWithSetting | null>(null);
  stoneTypeWithShape = signal<GemstoneTypeWithShape | null>(null);
  shareLink = computed(() => this.shareService.shareLink());
  selectedAttributes = computed(() => this.selectedAttributeService.getValue());
  shouldShowEngraving = this.selectedAttributes().engravingType;
  isRetailOnlyMode = computed(() => this.checkoutService.isRetailerOnly());
  attributesToShow = computed(() => Object.keys(this.selectedAttributes()).reduce((obj, key) => {
    if (key.includes('Material')) {
      if (key.includes('primary')) {
        obj['Edge Material'] = this.selectedAttributes().primaryMaterial?.displayName;
      } else {
        obj['Center Material'] = this.selectedAttributes().secondaryMaterial?.displayName;
      }
      return obj;
    }

    if (key.includes('gemstone')) {
      if (this.selectedAttributes().gemstoneType) {
        const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values().find(aws => aws.stoneArrangementID === this.selectedAttributeService.configuredItem().gemstoneArrangement?.id && aws.stoneSettingID === this.selectedAttributeService.configuredItem().gemstoneSetting?.id);
        const typeWithShape = this.stoneTypesWithShapesService.values().find(tws => tws.stoneTypeID === this.selectedAttributeService.configuredItem().gemstoneType?.id && this.selectedAttributeService.configuredItem().gemstoneShape?.id === this.selectedAttributeService.configuredItem().gemstoneShape?.id);
        if (key.includes('Type')) {
          obj['Gemstone Type'] = typeWithShape?.displayName;
        } else if (key.includes('Carat')) {
          obj['Gemstone Carat'] = this.selectedAttributes().gemstoneSize?.displayName;
        } else {
          obj['Gemstone Arrangement'] = arrangementWithSetting?.displayName;
        }
      } else {
        obj['Gemstones'] = 'No Gemstones';
      }
      return obj;
    }

    if (key === 'engravingType') {
      const { engravingType, engravingText } = this.selectedAttributes();
      obj['Engraving'] = null;
      if (engravingType && engravingText) {
        obj['Engraving'] = engravingText;
      }
      return obj;
    }
    if (['engravingText'].includes(key)) {
      return obj;
    }
    if (key === 'size') {
      obj['Finger Size'] = this.selectedAttributes().size?.displayName;
      return obj;
    }

    if (key === 'form') {
      obj['Profile'] = this.selectedAttributes().form?.displayName;
      const form = this.selectedAttributes().form;
      if (!form) {
        obj['Thickness'] = 'None';
      } else {
        const weight = this.profileWeightsService.values().find(w => w.id === form.formWeightID) ?? null;
        obj['Thickness'] = formatWeightThickness(weight?.shortName ?? form.shortName, form.thicknessMM);
      }
      return obj;
    }

    if (key === 'width') {
      obj['Width'] = this.selectedAttributes().width?.displayName;
      return obj;
    }

    const keyArr = key.split('');
    keyArr[0] = keyArr[0].toUpperCase();
    const newKey = keyArr.join('');
    obj[newKey] = this.getSelectedAttributeValueForKey(key);
    return obj;
  }, {} as any));
  attributeKeys = computed(() => {
    const keys = Object.keys(this.attributesToShow());
    // Thickness is a synthetic row emitted alongside Profile, not a configured
    // attribute: insert positionally after Profile, keyed on the string (key
    // order follows ConfiguredItem shape — never a numeric index).
    const profileIndex = keys.indexOf('Profile');
    if (profileIndex >= 0) {
      const thicknessIndex = keys.indexOf('Thickness');
      if (thicknessIndex >= 0) { keys.splice(thicknessIndex, 1); }
      keys.splice(keys.indexOf('Profile') + 1, 0, 'Thickness');
    }
    return keys;
  });
  shouldShowContainerShadow = signal(true);
  retailPrice = computed(() => (this.isSharing() || this.isRetailOnlyMode()) ? this.priceService.retailPrice() : null);
  poMaxLength = computed(() => 30 - ((!this.isSKUToShowCustom() || !this.checkoutService.b2bData.isQuarterSize) ? 0 : (5 + `${this.selectedAttributeService.configuredItem().size?.value ?? ''}`.length)));
  b2bDataSubscription: Subscription | undefined;

  constructor() {
    (window as any).checkoutSummaryComponent = this;

    effect(async () => {
      if (this.selectedAttributes().gemstoneType) {
        this.loadingService.load('CheckoutSummary::DisplayedValue::StoneCounts');
        const stoneCount = await this.stoneCountsProvider.get(this.selectedAttributes());
        this.stoneCount.set(stoneCount);
        this.loadingService.done('CheckoutSummary::DisplayedValue::StoneCounts');
      } else {
        this.stoneCount.set(0);
      }
    }, { allowSignalWrites: true });

    effect(() => {
      this.poValue = this.selectedAttributeService.poNumber();
    });

    effect(async () => {
      if (this.isSharing()) {
        const pricingObject = this.selectedAttributeService.pricingObject();
        if (pricingObject) {
          this.loadingService.load('checkout::price');
          await this.priceService.update(pricingObject, untracked(() => this.selectedAttributeService.configuredItem()), true);
          this.loadingService.done('checkout::price');
        } else {
          this.priceService.clear(true);
        }
      }
    }, { allowSignalWrites: true });
  }

  private getSelectedAttributeValueForKey(key: string) {
    const nameKeys = ['design', 'form'];
    const val = this.selectedAttributes()[key as keyof(ConfiguredItem)];
    if (nameKeys.includes(key)) {
      return (val as any)?.displayName ?? null;
    }
    return val;
  }

  saveForLater() {
    if (!this.isConfiguredItemComplete()) {
      this.attributeMenuService.shouldHilightRequired.set(true);
    } else {
      console.log('Save for later');
    }
  }

  ngOnInit() {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('view', 'attribute_panel', 'section_view', 'Review');
    }

    // Warm the item description cache now. Add to Cart is only reachable from this step, so by the
    // time it is clicked the lookup has normally already settled and the click does not wait on a
    // network round trip — and a transient failure has this whole step in which to retry, rather
    // than one attempt at the worst possible moment.
    if (!this.isSharing()) {
      this.checkoutService.prefetchDescription(
        this.selectedAttributes(), this.priceService.isPricedItemQuarterSized());
    }

    this.ref.element.nativeElement.addEventListener('scroll', (e: any ) => {
      const lastElementBottomOffset = 20;
      const container = e.target.children[0].children[e.target.children[0].children.length - 1].getBoundingClientRect();
      const lastNonContainerElement = e.target.children[0].children[e.target.children[0].children.length - 2].getBoundingClientRect();

      this.shouldShowContainerShadow.set(container.top + e.target.scrollTop <= lastNonContainerElement.top + lastNonContainerElement.height + lastElementBottomOffset);
    });
  }

  async ngAfterViewInit() {
    if (!this.isSharing()) {
      const size = this.selectedAttributeService.getValue().size;
      assert(size, 'No size selected');
      this.skuToShow.set((!this.checkoutService.b2bData.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(this.checkoutService.b2bData.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
      this.b2bDataSubscription = this.checkoutService.subscribeToB2BDataChanges(async value => {
        const size = this.selectedAttributeService.getValue().size;
        assert(size, 'No size selected');
        this.loadingService.load('SKUToShow');
        this.skuToShow.set((!value.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(value.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
        this.loadingService.done('SKUToShow');
      });
    }

    const skuToShow = this.skuToShow();
    if (skuToShow && this.configuredItemService.isCustomSKU(skuToShow) && ((!this.isSharing() && !this.isRetailOnlyMode()) || this.checkoutService.isRetailerCartEnabled())) {
      this.loadingService.load('checkout-summary:submit-thumbnail');
      // Registered with the provider, and awaited, so that add-to-cart can wait for it. The whole
      // snapshot-and-upload is one tracked unit: the exposed window used to start the moment the
      // review page appeared, not the moment the upload began (ADO #8902).
      await this.thumbnailProvider.trackSubmission(async () => {
        const thumbnailData = await this.mainImageService?.getSnapshot?.call(this.mainImageService);
        if (thumbnailData) {
          try {
            const styleNumber = this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem());
            if (this.isRetailOnlyMode()) {
              const retailerIframeID = this.checkoutService.retailerIframeID;
              assert(retailerIframeID, 'No retailer iframe ID');
              await this.thumbnailProvider.submitRetailerThumbnail(styleNumber, thumbnailData, retailerIframeID);
            } else {
              const { customerToken, customer: customerID, business, pc } = this.checkoutService.b2bData;
              if (customerToken) {
                await this.thumbnailProvider.submitThumbnail(styleNumber, thumbnailData, customerToken.toString(), business, pc);
              } else {
                assert(customerID, 'No customer ID');
                await this.thumbnailProvider.submitThumbnailLegacy(styleNumber, thumbnailData, customerID.toString(), business, pc);
              }
            }
          } catch(e) {
            console.error('Could not submit thumbnail');
            console.error(e);
          }
        } else {
          console.error('Unable to get ring snapshot');
        }
      });
      this.loadingService.done('checkout-summary:submit-thumbnail');
    }
  }

  ngOnDestroy() {
    this.b2bDataSubscription?.unsubscribe();
  }

  async addToCartClick() {
    if (!this.isConfiguredItemComplete()) {
      this.attributeMenuService.shouldHilightRequired.set(true);
    } else {
      try {
        this.loadingService.load('checkout-summary:add-to-cart');
        const pricingObject = this.selectedAttributeService.pricingObject();
        assert(pricingObject, 'Pricing object was null');
        await this.priceService.update(pricingObject, this.selectedAttributeService.configuredItem());
        const priceToSend = this.priceService.wholesalePrice();
        assert(priceToSend, 'No price');
        const b2bData = await this.checkoutService.getOutputData(this.selectedAttributes(), this.selectedAttributeService.poNumber(), this.priceService.isPricedItemQuarterSized());
        const roundedPrice = Math.round((priceToSend.valueBeforeFee + Number.EPSILON) * 100) / 100
        if (b2bData.unitPrice !== roundedPrice) {
          b2bData.unitPrice = roundedPrice;
          console.log('price updated')
        }

        const styleNumber = this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributes());

        if (this.googleAnalyticsService.isInitialized) {
          this.googleAnalyticsService.triggerEvent('click', 'Review', 'add_to_cart', `${styleNumber} sz: ${this.selectedAttributes().size?.value ?? 'None'}`);
        }
        const payload = JSON.stringify(b2bData);
        const encodedPayload = btoa(payload);
        window.location.href = `${environment.b2bURL}/customitem?o=${encodedPayload}`;
      } catch(e) {
        console.error(e);
        this.loadingService.done('checkout-summary:add-to-cart');
      }
    }
  }

  isConfiguredItemComplete() {
    return Object.keys(this.selectedAttributeService.configuredItem()).filter(k => !k.toLowerCase().includes('gemstone') && !k.includes('engraving') && !k.includes('design')).every(k => (this.selectedAttributeService.configuredItem() as any)[k]);
  }

  getAttributeValue(attribute: string) {
    return this.attributesToShow()[attribute];
  }

  getStyle(attribute: string) {
    if (attribute === 'Engraving' && this.selectedAttributes().engravingText) {
      if (this.selectedAttributes().engravingType?.displayName === 'Block') {
        return {font: '1.2rem "engraving_block"'}
      }

      if (this.selectedAttributes().engravingType?.displayName === 'Script') {
        return {font: '1.5rem "engraving_script"'};
      }

      return { }
    }

    return null;
  }

  engravingCheckChange() {
    if (!this.shouldShowEngraving) {
      this.selectedAttributeService.mutateEngravingType(null);
      this.selectedAttributeService.mutateEngravingText(null);
    }
  }

  poChange() {
    this.selectedAttributeService.setPO(this.poValue);
  }

  shareClicked() {
    const url = new URL(window.location.href);

    const shareURL = new URL(`https://${url.host}/share`);

    const shareSKU = this.checkoutService.b2bData.haloStyle ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem());
    assert(shareSKU, 'No sku to copy');
    shareURL.searchParams.set('sku', shareSKU);

    const configuredItemSize = this.selectedAttributeService.configuredItem().size;
    assert(configuredItemSize, 'No size selected');
    const b2bSize = this.configuredItemService.getB2BFingerSizeObject(configuredItemSize);
    shareURL.searchParams.set('size', b2bSize.size.toString());
    shareURL.searchParams.set('isQuarterSize', b2bSize.isQuarterSize.toString())

    const engravingText = this.selectedAttributeService.configuredItem().engravingText;
    const engravingType = this.selectedAttributeService.configuredItem().engravingType;
    if (engravingType && engravingText) {
      shareURL.searchParams.set('e', engravingType.shortName);
      shareURL.searchParams.set('et', engravingText);
    }

    const po = this.selectedAttributeService.poNumber();
    if (po) {
      shareURL.searchParams.set('po', po);
    }

    this.clipBoard.copy(shareURL.href);

    const checkoutContainer = this.checkoutContainerRef()?.nativeElement;
    if (checkoutContainer) {
      document.body.style.setProperty('--share-snack-bar-container-width', `${checkoutContainer.clientWidth}px`);
    }

    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'share', 'share_link_copy', shareURL.href);
    }
    this.shouldShowCopyConfirmation.set(true);
  }

  copyShareClicked() {
    this.clipBoard.copy(this.shareLink());

    const checkoutContainer = this.checkoutContainerRef()?.nativeElement;
    if (checkoutContainer) {
      document.body.style.setProperty('--share-snack-bar-container-width', `${checkoutContainer.clientWidth}px`);
    }

    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'copy_share', 'share_link_copy', this.shareLink());
    }
    (this.shareLinkTextArea()?.nativeElement as any)?.select();
    this.shouldShowCopyConfirmation.set(true);
  }

  finishButtonClick() {
    const viewIndex = this.navigationService.views().findIndex(view => view.title === 'Retailer Summary');
    assertTrue(viewIndex >= 0, 'No retailer summary view')
    this.navigationService.navigateTo(viewIndex);
  }

  shareURLFieldKeyDown(event: KeyboardEvent) {
    event.preventDefault();
  }

  @HostListener('window:resize', [])
  windowResize() {
    this.windowHeight.set(window.innerHeight);
    this.windowWidth.set(window.innerWidth);
  }

  closeButtonClicked() {
    this.navigationService.dismissAll();
  }

  retailerLinkClicked(event: Event) {
    event.preventDefault();
    this.iframeService.sendMessage('Benchmark::RingLab::retailerlinkclicked');
  }
}
